import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { HardDrive, RefreshCw, LogOut, ExternalLink } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { purgeBrowserSiteCache, hardReloadPublicSite } from '../../utils/cachePurge';

declare const __BUILD_TIME__: string | undefined;

const AdminCachePanel: React.FC = () => {
  const { darkMode } = useTheme();
  const { signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const box = darkMode ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-800';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-600';

  const runPurge = async (reload: boolean) => {
    setBusy(true);
    try {
      const r = await purgeBrowserSiteCache();
      const msg = `SW: ${r.swCount}, önbellek: ${r.cacheCount}, localStorage: ${r.storageKeys} anahtar silindi.`;
      setLastResult(msg);
      toast.success('Bu tarayıcının site önbelleği temizlendi');
      if (reload) {
        setTimeout(() => hardReloadPublicSite(), 400);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Temizleme başarısız');
    } finally {
      setBusy(false);
    }
  };

  const buildLabel =
    typeof __BUILD_TIME__ !== 'undefined'
      ? new Date(__BUILD_TIME__).toLocaleString('tr-TR')
      : 'Bilinmiyor (yeniden build alın)';

  return (
    <div className="space-y-6">
      <p className={`text-sm ${muted}`}>
        Ziyaretçilerin günlerce eski sürümü görmesi genelde <strong>service worker</strong>, tarayıcı önbelleği
        veya sunucuda eski <code className="text-xs">dist/</code> dosyalarından kaynaklanır. Aşağıdaki adımlar
        önce sizin tarayıcınızı, sonra canlı siteyi günceller.
      </p>

      <div className={`rounded-xl border p-5 ${box}`}>
        <h3 className="font-semibold flex items-center gap-2 mb-3">
          <HardDrive size={20} className="text-indigo-500" />
          Son frontend derlemesi
        </h3>
        <p className={`text-sm ${muted}`}>
          Build zamanı: <span className="font-mono">{buildLabel}</span>
        </p>
        <p className={`text-sm mt-2 ${muted}`}>
          Canlıda değişiklik yoksa: <code className="text-xs">npm run build</code> sonrası tüm{' '}
          <code className="text-xs">dist/</code> klasörünü (index.html + assets + .htaccess + sw.js) sunucuya
          yükleyin.
        </p>
      </div>

      <div className={`rounded-xl border p-5 ${box}`}>
        <h3 className="font-semibold mb-3">Bu tarayıcıyı temizle (admin)</h3>
        <ul className={`text-sm list-disc pl-5 space-y-1 mb-4 ${muted}`}>
          <li>Tüm service worker kayıtlarını kaldırır</li>
          <li>Cache API önbelleklerini siler</li>
          <li>Site ile ilgili localStorage anahtarlarını siler</li>
          <li>sessionStorage temizler</li>
        </ul>
        {lastResult && <p className="text-xs font-mono mb-3 text-green-600 dark:text-green-400">{lastResult}</p>}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => runPurge(false)}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            Önbelleği temizle
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => runPurge(true)}
            className="px-4 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw size={16} />
            Temizle ve siteyi yenile
          </button>
          <a
            href={`${window.location.origin}/?_cb=${Date.now()}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ExternalLink size={16} />
            Anasayfayı yeni sekmede aç
          </a>
        </div>
      </div>

      <div className={`rounded-xl border p-5 ${box}`}>
        <h3 className="font-semibold mb-3">Admin oturumu</h3>
        <p className={`text-sm mb-3 ${muted}`}>
          Başka bir cihazda eski oturum kaldıysa çıkış yapıp tekrar giriş yapın. Tüm kullanıcı oturumlarını
          uzaktan silmek Supabase panelinden yapılır.
        </p>
        <button
          type="button"
          onClick={() => signOut()}
          className="px-4 py-2 rounded-lg border border-red-300 text-red-600 dark:text-red-400 text-sm flex items-center gap-2 hover:bg-red-50 dark:hover:bg-red-950/30"
        >
          <LogOut size={16} />
          Çıkış yap
        </button>
      </div>

      <div className={`rounded-xl border p-5 ${box}`}>
        <h3 className="font-semibold mb-3">Logo / görsel güncellemeleri</h3>
        <p className={`text-sm ${muted}`}>
          Forum logosu aynı URL’de değiştiyse tarayıcı eski resmi tutabilir. Sitede logolar artık{' '}
          <code className="text-xs">updated_at</code> ile sorgu parametresi alıyor; yine de CDN kullanıyorsanız
          CDN önbelleğini de temizlemeniz gerekir.
        </p>
      </div>

      <div className={`rounded-xl border p-5 ${box}`}>
        <h3 className="font-semibold mb-3">Ziyaretçiler için (canlı)</h3>
        <ol className={`text-sm list-decimal pl-5 space-y-2 ${muted}`}>
          <li>Güncel <code className="text-xs">dist/</code> yükleyin; eski <code className="text-xs">assets/index-*.js</code> dosyalarını silin.</li>
          <li>
            <code className="text-xs">sw.js</code> sunucuda güncel mi kontrol edin (service worker devre dışı
            bırakıldı — ziyaretçiler eski SW’yi bir kez temizleyince sorun kalmaz).
          </li>
          <li>Host panelinde “Önbelleği temizle / Purge” varsa çalıştırın.</li>
        </ol>
      </div>
    </div>
  );
};

export default AdminCachePanel;
